import React, { FC, useCallback } from 'react';
import { FeatureFlag, useFeatureFlag } from 'src/helpers/featureFlag';
import { useBackHref } from 'src/helpers/history';
import { Navigate, createSearchParams, useNavigate } from 'react-router-dom';
import { faArchive, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import styles from './index.module.scss';
import { Button } from 'src/common/Button';
import { Skeleton } from 'src/common/Skeleton';
import { Paths } from 'src/pages/paths';
import { OverviewQp, ProcurementsDisplay } from 'src/models/procurements/types';
import NavCard from 'src/common/NavCard';
import { PageHeading, WorkspaceBadge } from 'src/common';
import { useActiveWsIdState, useWorkspaceArchivingAvailable, useWorkspaces } from 'src/models/workspace/hooks';

const BidspacesNavPage: FC = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const backHref = useBackHref();
  const isBidspacesFeature = useFeatureFlag(FeatureFlag.Bidspaces);

  const { data: allWorksapces, loading } = useWorkspaces();
  const [, setActiveWsId] = useActiveWsIdState();
  const archivingAvailable = useWorkspaceArchivingAvailable();

  const workspaces = allWorksapces?.filter(workspace => workspace.isActive);

  const onCreateWorkspace = useCallback(() => {
    navigate({
      pathname: Paths.HOME_PATH,
      search: createSearchParams({
        [OverviewQp.Tab]: ProcurementsDisplay.WORKSPACE_CREATING
      }).toString()
    });
  }, [navigate]);

  const onShowArchived = useCallback(() => {
    navigate({
      pathname: Paths.HOME_PATH,
      search: createSearchParams({
        [OverviewQp.Tab]: ProcurementsDisplay.ARCHIVED_WORKSPACES
      }).toString()
    });
  }, [navigate]);

  const onGoToBidspace = useCallback(
    (id: string) => {
      setActiveWsId(id);
      navigate({
        pathname: Paths.HOME_PATH,
        search: createSearchParams({
          [OverviewQp.Tab]: ProcurementsDisplay.WORKSPACE,
          [OverviewQp.WorkspaceId]: id
        }).toString()
      });
    },
    [navigate, setActiveWsId]
  );

  return isBidspacesFeature ? (
    loading ? (
      <Skeleton active={loading} />
    ) : (
      <>
        <PageHeading title={t('Navigation.bidSpaces')} />
        <div className={styles.scrollContainer}>
          <div className={styles.container}>
            {!!workspaces?.length &&
              workspaces.map(workspace => (
                <NavCard
                  key={workspace.id}
                  title={workspace.name}
                  id={workspace.id}
                  onClick={onGoToBidspace}
                  wsBadge={<WorkspaceBadge title={workspace.name} color={workspace.color} mode={'short'} size={'s'} />}
                  owners={workspace.owners}
                  subscribers={workspace.subscribers}
                  subscribersPolicy={workspace.accessControl.subscribePolicy}
                />
              ))}
            {archivingAvailable && (
              <Button className={styles.addNewCard} onClick={onShowArchived}>
                <FontAwesomeIcon icon={faArchive} />
                <span className={styles.addNew}> {t('BidSpaces.archivedBidSpaces')}</span>
              </Button>
            )}
            <Button className={styles.addNewCard} onClick={onCreateWorkspace}>
              <FontAwesomeIcon icon={faPlus} />
              <span className={styles.addNew}> {t('BidSpaces.createNewBidSpace')}</span>
            </Button>
          </div>
        </div>
      </>
    )
  ) : (
    <Navigate to={backHref} />
  );
};

export default BidspacesNavPage;
